/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

import { DeviceSchema, IdSchema } from './api.js';
import { DesiredSchema, ReportedSchema } from './shadow.js';
import { SceneIdSchema } from './scenes.js';
import { DeviceDigestSchema, DeviceOpSchema } from './device-ops.js';
import { SceneDigestSchema, SceneOpSchema } from './scene-doc.js';

/**
 * What the service pushes down the WebSocket.
 *
 * Shared with the browser, which parses every frame against this: a socket is
 * the one input that arrives without a request, so it gets the same treatment
 * as a request body rather than being trusted because it came from us.
 */

/**
 * A device's shadow changed.
 *
 * Carries the whole state rather than the update's delta. The delta would have
 * to be merged by the receiver, and a receiver that missed a frame would merge
 * it into the wrong base; a snapshot plus a version is idempotent and lets a
 * late or duplicated frame be dropped on sight.
 */
export const DeviceStateEventSchema = z.object({
  type: z.literal('device_state'),
  world_id: IdSchema,
  device_id: IdSchema,
  /** The shadow document version. Lower than the last seen means stale. */
  version: z.number().int().optional(),
  /** When the device last reported, in seconds. Absent until it ever has. */
  reported_at: z.number().int().optional(),
  state: z.object({
    desired: DesiredSchema.optional(),
    reported: ReportedSchema.optional(),
  }),
});
export type DeviceStateEvent = z.infer<typeof DeviceStateEventSchema>;

/** The world was bound to a different scene, by someone or some other tab. Null is an unbinding. */
export const SceneChangedEventSchema = z.object({
  type: z.literal('scene_changed'),
  world_id: IdSchema,
  scene_id: SceneIdSchema.nullable(),
  scene_revision: z.number().int().nullish(),
});
export type SceneChangedEvent = z.infer<typeof SceneChangedEventSchema>;

/**
 * A device row changed — its optics, name or identity, never its pose, which
 * travels through the shadow. The whole row, so a receiver can replace rather
 * than merge; `updated_at` orders two that cross.
 */
export const DeviceChangedEventSchema = z.object({
  type: z.literal('device_changed'),
  world_id: IdSchema,
  device: DeviceSchema,
});
export type DeviceChangedEvent = z.infer<typeof DeviceChangedEventSchema>;

/**
 * A gateway's link to the cloud came up or went down.
 *
 * Distinct from a device's `session`, which is about a PTP client holding that
 * device. A world can have every device idle and still be fully reachable.
 */
export const GatewayStatusEventSchema = z.object({
  type: z.literal('gateway_status'),
  world_id: IdSchema,
  gateway_id: IdSchema,
  status: z.enum(['online', 'offline']),
  devices: z.number().int().optional(),
});
export type GatewayStatusEvent = z.infer<typeof GatewayStatusEventSchema>;

/**
 * The assistant's answer, as it is written.
 *
 * One frame per flush rather than per token: a `PostToConnection` is a request,
 * and a token is not worth one. Deltas of a turn arrive in order and are
 * appended; there is no position, because a socket that dropped one frame has
 * dropped the turn.
 */
export const AgentDeltaEventSchema = z.object({
  type: z.literal('agent_delta'),
  thread_id: IdSchema,
  turn_id: IdSchema,
  text: z.string(),
});
export type AgentDeltaEvent = z.infer<typeof AgentDeltaEventSchema>;

/**
 * What the turn proposes, and whether the user has to agree first.
 *
 * The ops, not their effect: the browser applies them through the same writer a
 * gizmo drag goes through, so the agent is never a second write path (devplan
 * §3.2). One frame for both vocabularies, because the two never mix in a turn —
 * a socket is either a scene's or a world's, and each offers only its own tools.
 */
export const AgentOpsEventSchema = z.object({
  type: z.literal('agent_ops'),
  thread_id: IdSchema,
  turn_id: IdSchema,
  /**
   * One vocabulary per turn in practice: a socket is either a scene's or a
   * world's, and each offers only its own tools. Typed as a flat union rather
   * than a union of arrays so that nothing downstream has to cast to say so.
   */
  ops: z.array(z.union([SceneOpSchema, DeviceOpSchema])),
  /** Set for a batch large enough that seeing it first is worth a click (§7.4 rule 5). */
  needs_confirmation: z.boolean(),
});
export type AgentOpsEvent = z.infer<typeof AgentOpsEventSchema>;

/** The turn ended, for any reason. Always sent, including after an error. */
export const AgentDoneEventSchema = z.object({
  type: z.literal('agent_done'),
  thread_id: IdSchema,
  turn_id: IdSchema,
  stop_reason: z.enum(['end_turn', 'max_tokens', 'cancelled', 'timeout', 'error']),
  usage: z
    .object({
      input_tokens: z.number().int().nonnegative(),
      output_tokens: z.number().int().nonnegative(),
    })
    .optional(),
});
export type AgentDoneEvent = z.infer<typeof AgentDoneEventSchema>;

/**
 * A turn that could not run, in words a user may read.
 *
 * The code is what the UI branches on; the message is already safe to show, so
 * nothing downstream has to decide whether a model's or a service's wording can
 * be put on screen.
 */
export const AgentErrorEventSchema = z.object({
  type: z.literal('agent_error'),
  thread_id: IdSchema,
  turn_id: IdSchema.optional(),
  code: z.enum(['rate_limited', 'forbidden', 'bad_request', 'unavailable']),
  message: z.string(),
});
export type AgentErrorEvent = z.infer<typeof AgentErrorEventSchema>;

export const RealtimeEventSchema = z.discriminatedUnion('type', [
  DeviceStateEventSchema,
  SceneChangedEventSchema,
  DeviceChangedEventSchema,
  GatewayStatusEventSchema,
  AgentDeltaEventSchema,
  AgentOpsEventSchema,
  AgentDoneEventSchema,
  AgentErrorEventSchema,
]);
export type RealtimeEvent = z.infer<typeof RealtimeEventSchema>;

/**
 * The other direction, which until now did not exist.
 *
 * A frame from a browser is a request body that happens to arrive on a socket,
 * so it is parsed exactly like one. The ids are the client's: it needs to match
 * the deltas it gets back to the turn it asked for, and it cannot do that with
 * an id it only learns afterwards.
 */
export const AgentPromptFrameSchema = z.object({
  type: z.literal('agent_prompt'),
  thread_id: IdSchema,
  turn_id: IdSchema,
  text: z.string().min(1).max(4000),
  /** What the browser is looking at: a scene's nodes, or a world's devices. */
  digest: z.union([SceneDigestSchema, DeviceDigestSchema]).optional(),
});
export type AgentPromptFrame = z.infer<typeof AgentPromptFrameSchema>;

/** Stop the turn. Late is normal: a turn that already ended ignores it. */
export const AgentCancelFrameSchema = z.object({
  type: z.literal('agent_cancel'),
  thread_id: IdSchema,
  turn_id: IdSchema,
});
export type AgentCancelFrame = z.infer<typeof AgentCancelFrameSchema>;

export const ClientFrameSchema = z.discriminatedUnion('type', [AgentPromptFrameSchema, AgentCancelFrameSchema]);
export type ClientFrame = z.infer<typeof ClientFrameSchema>;

/** The query parameter a browser opens the socket with. */
export const WORLD_QUERY_PARAM = 'world_id';
export const TOKEN_QUERY_PARAM = 'token';
